import { createFileRoute } from '@tanstack/react-router';
import { useMockAuth } from '../../../hooks/useMockAuth';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Creators Dashboard - Overview of creator metrics and activity
 */
export const Route = createFileRoute('/_authenticated/creators/')({
  component: CreatorsDashboard,
});

function CreatorsDashboard() {
  const { user } = useMockAuth();
  const { theme } = useTheme();

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af'
    },
    light: {
      bg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Creator Dashboard
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Welcome back, {user?.email}
          </p>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <StatCard
            title="Total Revenue"
            value="$0.00"
            change="+0%"
            trend="up"
            theme={theme}
          />
          <StatCard
            title="Active Products"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
          <StatCard
            title="Active Licenses"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
          <StatCard
            title="Orders This Month"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
        </div>

        {/* Recent Activity */}
        <div style={{
          background: c.cardBg,
          border: `1px solid ${c.cardBorder}`,
          borderRadius: '0.5rem',
          padding: '1.5rem',
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
          boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: c.text,
            marginBottom: '1rem'
          }}>
            Recent Activity
          </h2>
          <p style={{ color: c.textMuted, fontSize: '0.875rem' }}>
            No recent activity to display. Start by creating your first product!
          </p>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  theme: 'dark' | 'light';
}

function StatCard({ title, value, change, trend, theme }: StatCardProps) {
  const trendColor = trend === 'up' ? '#059669' : trend === 'down' ? '#dc2626' : '#6b7280';

  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: '0.875rem',
        fontWeight: '500',
        color: c.textMuted,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        color: c.text,
        marginBottom: '0.5rem'
      }}>
        {value}
      </p>
      <p style={{
        fontSize: '0.875rem',
        color: trendColor,
        fontWeight: '500'
      }}>
        {change} from last month
      </p>
    </div>
  );
}
